/**
 * Manually-maintained types mirroring api/domain/schemas/*.
 *
 * Phase 3 ships these by hand because @hey-api/openapi-ts v0.55 has a
 * plugin-ecosystem mismatch with our tooling. A follow-up spec replaces
 * this file with auto-generated content from `make generate-client`.
 *
 * If you change a Pydantic model in api/domain/schemas/, update the
 * matching interface here.
 */

export type Role = "customer" | "tenant_admin" | "platform_admin";

export type ErrorCode =
  | "INVALID_INPUT"
  | "UNAUTHENTICATED"
  | "FORBIDDEN"
  | "NOT_FOUND"
  | "CONFLICT"
  | "RATE_LIMITED"
  | "TOKEN_CAP_EXCEEDED"
  | "CONVERSATION_BUSY"
  | "BEDROCK_UNAVAILABLE"
  | "INTERNAL";

export type TenantStatus = "active" | "suspended" | "archived";

export interface NotificationPrefs {
  email: boolean;
  sms: boolean;
}

export interface AuthMe {
  user_id: string;
  tenant_id: string;
  role: Role;
  email: string;
  first_name: string;
  last_name: string;
  phone: string | null;
  notification_prefs: NotificationPrefs;
  mfa_enabled: boolean;
}

export interface TenantBranding {
  tenant_id: string;
  display_name: string;
  // All asset URLs are nullable: when no public branding CDN is
  // configured for the env, the API returns null and the BrandHeader
  // falls back to the tenant display name as text.
  logo_url: string | null;
  wordmark_url: string | null;
  favicon_url: string | null;
  primary_color: string;
  accent_color: string;
  background_color: string;
}

export interface TenantSummary {
  tenant_id: string;
  display_name: string;
  subdomain: string;
  status: TenantStatus;
  created_at: string;
}

export interface ApiError {
  error: {
    code: ErrorCode;
    message: string;
    details?: Array<{ field: string; issue: string }>;
  };
  meta: { requestId?: string };
}

// ---- spec 002: Estimate + Chat ----

export type EstimateStatus =
  | "in_progress"
  | "submitted"
  | "in_review"
  | "info_requested"
  | "approved"
  | "rejected"
  | "expired";

export type MessageRole = "user" | "assistant" | "system";

export type RoomType =
  | "bathroom"
  | "kitchen"
  | "bedroom"
  | "living_room"
  | "garage"
  | "whole_house"
  | "other";

export const ROOM_TYPE_LABEL: Record<RoomType, string> = {
  bathroom: "Bathroom",
  kitchen: "Kitchen",
  bedroom: "Bedroom",
  living_room: "Living room",
  garage: "Garage",
  whole_house: "Whole house",
  other: "Other",
};

export interface Address {
  line1: string;
  line2: string | null;
  city: string;
  state: string;
  zip: string;
}

// Spec 003: per-photo dimensions / estimate rollup.
// Spec 012 adds the "device_measured" source for RoomPlan captures.
export interface DimensionRange {
  value: number;
  low: number;
  high: number;
  confidence_text: "low" | "medium" | "high";
  confidence_score: number | null;
  // Phase 19 — `customer` added: derived from a known_dimensions
  // anchor or an approved Design sketch. Priority in pricing:
  // admin > customer > ai. Existing values still accepted.
  source: "ai" | "admin_adjusted" | "admin" | "customer" | "device_measured";
}

export interface Dimensions {
  floor_sqft: DimensionRange | null;
  wall_sqft: DimensionRange | null;
  ceiling_sqft: DimensionRange | null;
  linear_ft: DimensionRange | null;
  // Phase 19 — customer-described project area (the wall/floor being
  // remodelled, not the room's total). Tagged source="customer" when
  // set. The admin "Customer-described dimensions" panel reads these.
  project_wall_sqft?: DimensionRange | null;
  project_floor_sqft?: DimensionRange | null;
}

export interface Estimate {
  estimate_id: string;
  tenant_id: string;
  customer_user_id: string;
  status: EstimateStatus;
  system_prompt_version: string;
  next_sequence: number;
  total_input_tokens: number;
  total_output_tokens: number;
  last_message_at: string;
  created_at: string;
  updated_at: string;
  version: number;
  // Spec 003 identity + dimension rollup
  room_type: RoomType | null;
  room_label: string | null;
  address: Address | null;
  anchor_count: number;
  dimensions_rollup: Dimensions | null;
  // Spec 005 lifecycle
  submitted_at: string | null;
  review_started_at: string | null;
  assigned_admin_user_id: string | null;
  approved_at: string | null;
  rejected_at: string | null;
  rejection_reason: string | null;
  deposit_cents: number | null;
  deposit_paid_at: string | null;
  stripe_session_id: string | null;
  stripe_payment_intent_id: string | null;
  expired_at: string | null;
  // Spec 006 clarifying-question round-trip
  info_requested_at: string | null;
  review_resumed_at: string | null;
  // Soft delete — set when the customer archives the project from the
  // edit modal. The backend hides archived rows from listEstimates.
  archived_at?: string | null;
  // Denormalized from the latest EstimateSnapshot so the project list
  // can render "$840" alongside the status without a follow-up
  // snapshot read per row. Null when no estimate has been computed.
  last_subtotal_low_cents?: number | null;
  last_subtotal_high_cents?: number | null;
  // Phase 17 — admin-set negotiation offer. Lives on the Estimate row;
  // the snapshot is untouched (Constitution II). At most one active
  // offer at a time; prior offers go to offer_history with outcome set.
  current_offer?: Offer | null;
  offer_history?: Offer[];
  // Stamped on approval (approved_at + 30d). When passed, the expiry
  // Lambda flips status to EXPIRED.
  quote_expires_at?: string | null;
  // Phase 18 — generative remodel previews.
  renders_count?: number;
  approved_render_ids?: string[];
  // Phase 21 — which project Package was last applied. Server-managed
  // (set by package:apply, never by a customer PATCH).
  applied_package_id?: string | null;
}

export interface Offer {
  final_cents: number;
  discount_cents?: number | null;
  expires_at: string;
  created_at: string;
  created_by: string;
  outcome?: "active" | "expired" | "superseded" | "accepted" | null;
}

export interface AdminOfferBody {
  final_cents: number;
  discount_cents?: number | null;
  expires_at?: string | null;
}

// Phase 17 — discriminator for chat-card rendering. `text` is the
// default for backwards compatibility with pre-Phase-17 messages.
// Phase 18 adds the four `render_*` kinds.
export type ChatMessageKind =
  | "text"
  | "quote"
  | "quote_superseded"
  | "expired_quote"
  | "approval"
  | "intent_picker"
  | "render_generating"
  | "render_complete"
  | "render_approved"
  | "render_rejected"
  // Phase 18.4 — design sketch with approve/reject buttons.
  | "design_pending"
  | "design_approved"
  | "design_rejected";

export interface QuotePayload {
  estimate_id: string;
  snapshot_version: number;
  subtotal_low_cents: number;
  subtotal_high_cents: number;
  currency: string;
  status: string;
  primary_materials: string[];
}

// Phase 18 — generative remodel preview entities.
export type RenderStatus =
  | "pending"
  | "generating"
  | "complete"
  | "approved"
  | "rejected"
  | "failed";

// Phase 18.1 — closed list of rejection categories.
export type RejectionCategory =
  | "colors"
  | "style"
  | "materials"
  | "unrealistic"
  | "other";

export interface RenderPayload {
  render_id: string;
  photo_id: string;
  prompt: string;
  status: RenderStatus;
  url?: string | null;
  width?: number | null;
  height?: number | null;
  estimated_ready_at?: string | null;
  // Phase 18.1 — set on the payload when the card flips to
  // `render_rejected` so the customer's feedback is visible in chat
  // history. Pulled from the RemodelRender row at flip time.
  rejection_reason?: string | null;
  rejection_category?: RejectionCategory | null;
}

export interface RemodelRender {
  render_id: string;
  tenant_id: string;
  estimate_id: string;
  photo_id: string;
  prompt: string;
  augmented_prompt: string;
  base_render_id: string | null;
  status: RenderStatus;
  // S3 key is admin-internal; client only ever sees `url`.
  s3_key?: string | null;
  url?: string | null;
  width: number | null;
  height: number | null;
  model_id: string | null;
  created_at: string;
  created_by_user_id: string;
  approved_at: string | null;
  rejected_at: string | null;
  // Phase 18.1 — customer-supplied feedback at reject time.
  rejection_reason?: string | null;
  rejection_category?: RejectionCategory | null;
  failure_reason: string | null;
}

export interface ListRendersResponse {
  data: RemodelRender[];
  meta: { count: number; quota_remaining: number };
}

export interface RenderActionResponse {
  data: RemodelRender;
  meta: Record<string, unknown>;
}

export interface DeleteRenderResponse {
  data: { render_id: string; deleted: boolean };
  meta: Record<string, unknown>;
}

export interface RemodelIntent {
  intent_id: string;
  tenant_id: string;
  display_name: string;
  photo_url: string | null;
  default_room_type: string | null;
  sort_order: number;
  active: boolean;
  version: number;
  created_at: string;
  updated_at: string;
}

export interface RemodelIntentCreateBody {
  intent_id: string;
  display_name: string;
  photo_url?: string | null;
  default_room_type?: string | null;
  sort_order?: number;
  active?: boolean;
}

export interface RemodelIntentPatchBody {
  display_name?: string;
  photo_url?: string | null;
  default_room_type?: string | null;
  sort_order?: number;
  active?: boolean;
}

// ---------- Phase 21 — bathroom project packages ----------

/** Full Package row — tenant-admin views only. */
export interface Package {
  package_id: string;
  tenant_id: string;
  display_name: string;
  room_type: string;
  project_type: string;
  description: string | null;
  line_item_material_ids: string[];
  optional_add_on_material_ids: string[];
  photo_url: string | null;
  sort_order: number;
  active: boolean;
  version: number;
  created_at: string;
  updated_at: string;
}

export interface PackageCreateBody {
  package_id: string;
  display_name: string;
  room_type: string;
  project_type: string;
  description?: string | null;
  line_item_material_ids: string[];
  optional_add_on_material_ids?: string[];
  photo_url?: string | null;
  sort_order?: number;
  active?: boolean;
}

export interface PackagePatchBody {
  display_name?: string;
  room_type?: string;
  project_type?: string;
  description?: string | null;
  line_item_material_ids?: string[];
  optional_add_on_material_ids?: string[];
  photo_url?: string | null;
  sort_order?: number;
  active?: boolean;
}

/**
 * Reduced projection the CUSTOMER sees on the project-type step.
 * Deliberately carries no `line_item_material_ids` — the customer must
 * not be able to enumerate the tenant's catalog structure.
 */
export interface PackageCard {
  package_id: string;
  display_name: string;
  project_type: string;
  description: string | null;
  photo_url: string | null;
  sort_order: number;
}

/** Why a package line item could not be applied. */
export type PackageSkipReason =
  | "not_in_catalog"
  | "no_option_group"
  | "inactive";

export interface PackageSkippedItem {
  material_id: string;
  reason: PackageSkipReason;
}

export interface PackageAddOn {
  material_id: string;
  display_name: string;
  unit_price_cents: number;
  /** Needed to add it through the ordinary (group-keyed) option picker. */
  option_group_id: string | null;
  option_group_name: string | null;
  photo_url: string | null;
}

/**
 * Result of `POST /v1/estimates/{id}/package:apply`. Exactly one of
 * `snapshot` / `missing_inputs` is non-null: a half bath prices with no
 * measurement at all, while a master bath without a measured floor area
 * reports which axis it still needs — the package is applied either way.
 */
export interface PackageMissingInputs {
  kind?: string;
  needed: string[];
  message: string;
}

export interface PackageApplyResult {
  applied_package_id: string;
  selections_applied: number;
  groups_cleared: string[];
  skipped: PackageSkippedItem[];
  add_ons: PackageAddOn[];
  snapshot: EstimateSnapshot | null;
  missing_inputs: PackageMissingInputs | null;
}

export interface Note {
  note_id: string;
  tenant_id: string;
  estimate_id: string;
  author_user_id: string;
  body: string;
  created_at: string;
  updated_at: string;
}

// Spec 007 — design sketch.

export type DesignKind =
  | "rect"
  | "drawer"
  | "tv_opening"
  | "shelf"
  | "door"
  | "cabinet";

export interface DesignElement {
  kind: DesignKind;
  x: number;
  y: number;
  w: number;
  h: number;
  label: string | null;
  material: string | null;
}

export interface Design {
  design_id: string;
  tenant_id: string;
  estimate_id: string;
  outer_w: number;
  outer_h: number;
  elements: DesignElement[];
  prompt_version: string;
  computed_at: string;
  // Phase 18.4 — approval state. `approved_at` stamps the sketch as
  // the canonical layout for the estimate; `superseded_at` is set on
  // the prior approved sketch when a newer one is approved.
  approved_at?: string | null;
  superseded_at?: string | null;
}

/** Payload of the `event: design` SSE frame. */
export interface DesignStreamPayload {
  design: Design;
}

/** Phase 18.4 — GET /v1/estimates/{id}/designs response envelope. */
export interface ListDesignsResponse {
  data: Design[];
  meta: { count: number };
}

/** Phase 18.4 — approve / reject response envelope. */
export interface DesignActionResponse {
  data: Design;
  meta: Record<string, unknown>;
}

export interface LineItemOverride {
  override_id: string;
  tenant_id: string;
  estimate_id: string;
  line_id: string;
  admin_user_id: string;
  before_unit_price_cents: number | null;
  after_unit_price_cents: number | null;
  before_qty_low: number | null;
  before_qty_value: number | null;
  before_qty_high: number | null;
  after_qty_low: number | null;
  after_qty_value: number | null;
  after_qty_high: number | null;
  reason: string;
  overridden_at: string;
}

export interface LineItemOverrideBody {
  unit_price_cents?: number;
  qty_low?: number;
  qty_value?: number;
  qty_high?: number;
  reason: string;
}

export interface DepositCheckoutResponse {
  redirect_url: string;
  session_id: string;
}

export interface EstimatePatchBody {
  room_type?: RoomType | null;
  room_label?: string | null;
  address?: Address | null;
}

// Spec 008: Phase 8 intake gate.
export interface EstimateCreateRequest {
  /** Inline address. Mutually exclusive with `reuse_from_estimate_id`. */
  address?: Address;
  /** Free-form phone; server normalises to E.164. */
  phone: string;
  /** ULID of one of the caller's prior estimates whose address to reuse. */
  reuse_from_estimate_id?: string;
}

export interface AdminEstimate extends Estimate {
  customer_email: string | null;
  customer_first_name: string | null;
  customer_last_name: string | null;
  customer_phone: string | null;
}

/** One row in the tenant-admin Users list (sourced from Cognito). */
export interface AdminUserSummary {
  username: string; // opaque Cognito handle for admin mutations
  user_id: string; // Cognito sub
  email: string;
  first_name: string;
  last_name: string;
  role: Role;
  enabled: boolean;
  status: string; // Cognito UserStatus (CONFIRMED, FORCE_CHANGE_PASSWORD, …)
  created_at: string | null;
}

// Spec 003: photo + anchor wire types.
export type PhotoUploadStatus = "presigned" | "uploaded" | "failed";

export type PhotoDimensionStatus = "pending" | "processing" | "done" | "failed";

export type PhotoCategory = "photo" | "room_scan";

export interface Photo {
  photo_id: string;
  tenant_id: string;
  estimate_id: string;
  category: PhotoCategory;
  upload_status: PhotoUploadStatus;
  dimension_status: PhotoDimensionStatus;
  bytes: number | null;
  mime_type: string | null;
  width: number | null;
  height: number | null;
  thumbnail_url: string | null;
  original_url: string | null;
  dimensions: Dimensions | null;
  applied_anchor_id: string | null;
  extraction_error: string | null;
  created_at: string;
  updated_at: string;
}

export type AnchorKind =
  | "known_dimensions"
  | "wall_height"
  | "letter_page"
  | "room_plan"
  | "ar_linear"
  | "ar_surface";

// Spec 012: device-measured payload from Apple's RoomPlan API.
export interface RoomPlanPayload {
  floor_sqft: number;
  wall_sqft: number;
  linear_ft: number;
  ceiling_height_inches: number;
  door_count: number;
  window_count: number;
  confidence: "high" | "medium" | "low";
  captured_at: string;
}

// Spec 013: device-measured point-to-point distance.
export interface ArLinearPayload {
  length_inches: number;
  device_has_lidar: boolean;
  captured_at: string;
}

// Spec 013: device-measured polygon surface area.
export interface ArSurfacePayload {
  area_sqft: number;
  polygon_points: Array<[number, number, number]>;
  plane_normal: [number, number, number] | null;
  device_has_lidar: boolean;
  captured_at: string;
}

export interface ScaleAnchor {
  anchor_id: string;
  kind: AnchorKind;
  value_text: string;
  value_inches: number | null;
  tied_to_photo_id: string | null;
  created_at: string;
  room_plan_payload: RoomPlanPayload | null;
  ar_linear_payload: ArLinearPayload | null;
  ar_surface_payload: ArSurfacePayload | null;
}

export interface PresignResponse {
  photo_id: string;
  upload_url: string;
  expires_at: string;
}

// ---- Spec 004: catalog, pricing, visual selection ----

export type PricingMode = "itemized" | "bundled";

export type MaterialUnitKind = "sqft" | "linear_ft" | "each" | "gallon";

export type PriceTier = "budget" | "mid" | "premium" | "luxury";

export interface RecipeComponent {
  component_material_id: string;
  qty_per_parent_unit: number;
  unit: MaterialUnitKind;
}

export interface Material {
  material_id: string;
  tenant_id: string;
  display_name: string;
  category: string;
  subcategory: string | null;
  pricing_mode: PricingMode;
  unit: MaterialUnitKind;
  unit_price_cents: number;
  option_group_id: string | null;
  option_group_name: string | null;
  compatible_with: string[];
  recipe_components: RecipeComponent[];
  photo_url: string | null;
  description: string | null;
  price_tier: PriceTier | null;
  style_tags: string[];
  room_types: string[];
  surface_types: string[];
  // Spec 015 — one-coat coverage for paint-like materials sold by the
  // gallon. Null on non-coverage materials.
  coverage_sqft_per_unit?: number | null;
  active: boolean;
  version: number;
  created_at: string;
  updated_at: string;
}

export interface MaterialPatchBody {
  display_name?: string;
  category?: string;
  subcategory?: string | null;
  pricing_mode?: PricingMode;
  unit?: MaterialUnitKind;
  unit_price_cents?: number;
  option_group_id?: string | null;
  option_group_name?: string | null;
  compatible_with?: string[];
  recipe_components?: RecipeComponent[];
  photo_url?: string | null;
  description?: string | null;
  price_tier?: PriceTier | null;
  style_tags?: string[];
  room_types?: string[];
  surface_types?: string[];
  coverage_sqft_per_unit?: number | null;
}

// Spec 016 — single-material create. Full creatable field set; the
// server stamps tenant_id, version, created_at, updated_at.
export interface MaterialCreateBody {
  material_id: string;
  display_name: string;
  category: string;
  subcategory?: string | null;
  pricing_mode?: PricingMode;
  unit: MaterialUnitKind;
  unit_price_cents: number;
  option_group_id?: string | null;
  option_group_name?: string | null;
  compatible_with?: string[];
  recipe_components?: RecipeComponent[];
  photo_url?: string | null;
  description?: string | null;
  price_tier?: PriceTier | null;
  style_tags?: string[];
  room_types?: string[];
  surface_types?: string[];
  coverage_sqft_per_unit?: number | null;
  active?: boolean;
}

export interface LaborRate {
  task_id: string;
  tenant_id: string;
  display_name: string;
  unit: MaterialUnitKind;
  unit_price_cents: number;
  notes: string | null;
  active: boolean;
  version: number;
}

// Spec 016 — labor rate inline edit. All fields optional.
export interface LaborPatchBody {
  display_name?: string;
  unit?: MaterialUnitKind;
  unit_price_cents?: number;
  notes?: string | null;
  active?: boolean;
}

export interface ZipFactor {
  zip: string;
  factor: number;
  notes: string | null;
  active: boolean;
  version: number;
}

// Spec 016 — ZIP factor inline edit. All fields optional. `zip` is the
// PK and not patchable.
export interface ZipFactorPatchBody {
  factor?: number;
  notes?: string | null;
  active?: boolean;
}

export interface ImportPresignResponse {
  import_id: string;
  upload_url: string;
  expires_at: string;
  kind: "material" | "labor" | "zip_factor";
}

export interface ImportRowError {
  row_number: number;
  column: string;
  message: string;
}

export interface ImportPreviewResponse {
  import_id: string;
  kind: "material" | "labor" | "zip_factor";
  parsed_count: number;
  error_count: number;
  sample_rows: Record<string, unknown>[];
  errors: ImportRowError[];
}

export interface ImportCommitResponse {
  import_id: string;
  kind: string;
  written: number;
}

export interface MaterialSelection {
  tenant_id: string;
  estimate_id: string;
  option_group_id: string;
  option_group_name: string | null;
  material_id: string;
  display_name: string;
  unit_price_cents_snapshot: number;
  unit_snapshot: MaterialUnitKind;
  pricing_mode_snapshot: PricingMode;
  description_snapshot: string | null;
  photo_url_snapshot: string | null;
  selected_at: string;
  selected_by_user_id: string;
}

export type LineItemKind = "material" | "labor" | "bundled";

export interface LineItem {
  line_id: string;
  kind: LineItemKind;
  material_id: string | null;
  labor_task_id: string | null;
  display_name: string;
  unit: string;
  qty_low: number | null;
  qty_value: number | null;
  qty_high: number | null;
  unit_price_cents: number;
  zip_factor: number;
  line_low_cents: number;
  line_value_cents: number;
  line_high_cents: number;
  source: "ai" | "admin_adjusted";
}

export interface EstimateSnapshot {
  tenant_id: string;
  estimate_id: string;
  line_items: LineItem[];
  subtotal_low_cents: number;
  subtotal_value_cents: number;
  subtotal_high_cents: number;
  zip_factor_used: number;
  zip_used: string | null;
  non_binding_notice_version: string;
  computed_at: string;
  prompt_version: string;
}

// In-stream payloads (delivered via the chat-stream SSE).

export interface OptionsCardPayload {
  group_id: string;
  group_name?: string;
  cards: Array<{
    option_id: string;
    display_name: string;
    description: string;
    photo_url: string | null;
    option_group_id: string | null;
    option_group_name: string | null;
    /**
     * Phase 21 — set only on `intent_picker` cards. Lets the client stamp
     * the estimate's `room_type` on tap rather than relying on Stella to
     * emit `[[IDENTITY:room_type=…]]`, which she does not always do.
     */
    default_room_type?: string | null;
  }>;
}

export interface EstimateStreamPayload {
  kind: "snapshot" | "missing_inputs" | "error";
  snapshot?: EstimateSnapshot;
  needed?: string[];
  message?: string;
}

// Spec 014: inline `[[MEASURE:…]]` directive payload attached to an
// assistant chat message. The mobile client renders a tappable
// MeasureButton when this is non-null.
export type MeasureMode = "linear" | "surface" | "room";

export interface MeasurePayload {
  mode: MeasureMode;
  label: string | null;
  /** Filled when the customer taps the inline button + the AR flow
   * saves an anchor. Survives chat reloads so the button renders in
   * its confirmation state on next mount. */
  saved_anchor_id: string | null;
  saved_summary: string | null;
}

export interface ChatMessage {
  estimate_id: string;
  tenant_id: string;
  sequence: number;
  role: MessageRole;
  content: string;
  input_tokens: number | null;
  output_tokens: number | null;
  model_id: string | null;
  truncated: boolean;
  created_at: string;
  // Spec 006 — true means this message came from an admin via the
  // ask-clarification flow; UI renders a "from your contractor" callout.
  is_clarification?: boolean;
  // Spec 014 — present on assistant messages when Stella emitted a
  // `[[MEASURE:…]]` directive. The chat-stream Lambda strips the
  // directive from `content` and persists the parsed payload here.
  measure_payload?: MeasurePayload | null;
  // Spec 015 polish — present on assistant messages when Stella emitted
  // a `[[OPTIONS:category=…]]` directive. Lets the picker rehydrate
  // when the customer reloads the estimate or returns from another tab.
  options_payload?: OptionsCardPayload | null;
  // Phase 17 — chat-card discriminator. Default `"text"` matches legacy
  // messages. `quote` / `quote_superseded` / `expired_quote` / `approval`
  // carry `quote_payload`; `intent_picker` carries `options_payload`.
  kind?: ChatMessageKind;
  quote_payload?: QuotePayload | null;
  // Phase 18 — set when `kind` is one of the four `render_*` values.
  render_payload?: RenderPayload | null;
  // Phase 18 — true when Stella emitted `[[ADD_PHOTO]]`; the client
  // renders an inline "Add a photo" upload button below the message.
  photo_request?: boolean;
  // Phase 18.4 — set when `kind` is one of the three `design_*`
  // values. Carries the full Design geometry plus the current approval
  // state so the DesignSketch card can render the SVG + approve/reject
  // buttons without a second fetch.
  design_payload?: Design | null;
  // Phase 18.7 — true when Stella emitted `[[OFFER_RENDER]]`; the
  // client renders an inline "Render the design on my photo" button
  // below the message. Tap → synthetic user turn → Stella emits
  // [[RENDER:…]] on the next turn.
  offer_render?: boolean;
  // Phase 19.1 — true when Stella emitted `[[CONTACT_STARZ]]` (or
  // the pricing pipeline returned RequiresConsultation). The client
  // renders an inline Email Starz / Call Starz button row reading
  // `contact_starz_payload`.
  contact_starz?: boolean;
  contact_starz_payload?: ContactStarzPayload | null;
}

export interface ContactStarzPayload {
  support_email?: string | null;
  support_phone?: string | null;
  // 7 entries (Mon..Sun); each is [open_h, close_h] in 24-h or null
  // for "closed that day". Frontend computes "is open now?" against
  // the tenant's `business_hours_timezone`.
  business_hours_weekly?: ([number, number] | null)[] | null;
  business_hours_timezone?: string | null;
  // Short human-readable reason the chat is handing off — e.g.
  // "Additions" / "Pier Foundation" / "no matching catalog item".
  reason?: string | null;
}

export interface PageMeta {
  requestId?: string;
  cursor?: string | null;
}
